import { ErrorBoundary, RollbarContext } from '@rollbar/react'
import * as actions from 'client/actions/actionsManagement'
import Page from 'client/components/core/Page/index'
import LibrarySkeleton from 'client/components/core/Sidebar/components/Toolbox/library-skeleton'
import { WORK_AREA_ID } from 'client/constants/editorIds'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import useManagement, { selectors } from 'client/hooks/useManagement'
import { useActions } from 'common/hooks/useActions'
import React, {
  KeyboardEvent,
  MouseEvent,
  memo,
  useCallback,
  useRef,
} from 'react'
import { Trans } from 'react-i18next'
import { Overlay } from 'src/editor/layout/overlay'
import { twMerge } from 'tailwind-merge'
import { Header } from '../header'
import { Sidebar } from '../sidebar'
// import { useMobileScrollIndicator } from './hooks/use-mobile-scroll-indicator'

export interface ContentProps {
  isLoading: boolean
}

export const Content = memo(({ isLoading }: ContentProps) => {
  const contentRef = useRef<HTMLElement | null>(null)
  const { isLandscape, isMobile } = useDeviceModeContext()
  const isSomethingEditing = useManagement(selectors.isSomethingEditing)
  const toggleSettings = useActions(actions.toggleSettings)

  // const { externalScrollRef } = useMobileScrollIndicator()

  const disableMobileSettings = useCallback(
    (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      if ('key' in e) {
        if (e.key !== 'Enter' && e.key !== ' ') {
          return
        }
      }

      if (isMobile && isSomethingEditing && contentRef.current === e.target) {
        toggleSettings(null)
      }
    },
    [isMobile, isSomethingEditing, toggleSettings],
  )

  if (isLoading) {
    return (
      <>
        <Header />
        <LibrarySkeleton />
      </>
    )
  }

  return (
    <>
      <Header />
      <Sidebar />
      <RollbarContext context="editor">
        <ErrorBoundary
          fallbackUI={() => (
            <Trans parent="h3">core.errors.went_wrong_contact_support</Trans>
          )}
        >
          <main
            className={twMerge(
              'relative flex h-full items-center justify-center',
              isMobile && 'pt-4',
            )}
            onClick={disableMobileSettings}
            onKeyUp={disableMobileSettings}
            ref={contentRef}
          >
            {isMobile && (
              <>
                <img
                  className={twMerge(
                    'pointer-events-none absolute z-13 h-200 w-110',
                    isLandscape && 'h-92 w-200',
                  )}
                  src={`${process.env.CLOUDFRONT_SHARED}/images/preview_frame_phone.png`}
                  alt="Preview Frame"
                />
                <div
                  // ref={externalScrollRef}
                  className={twMerge(
                    'absolute right-4 h-190 w-4 overflow-y-auto',
                    isLandscape && 'h-92',
                  )}
                />
              </>
            )}
            <div
              className={twMerge(
                isMobile ? 'max-h-200 max-w-96' : 'w-full',
                isLandscape && 'max-h-92 max-w-200',
              )}
            >
              <div
                id={WORK_AREA_ID}
                className={twMerge(
                  'h-[calc(100vh-var(--layout-header-height))]',
                  isMobile
                    ? 'h-190 w-96 scroll-smooth'
                    : 'flex flex-col overflow-hidden',
                  isLandscape && 'h-92 w-200',
                )}
              >
                <Page
                  className={twMerge(isMobile && 'no-scrollbar rounded-[51px]')}
                />
                <Overlay />
              </div>
            </div>
          </main>
        </ErrorBoundary>
      </RollbarContext>
    </>
  )
})
